import { specValue } from '../cars/carFigures';
import { isRoundCandidate } from './duelDifficulty';
import { DUEL_SPECS, duelMargin, duelSpec, duelWinner } from './duelSpecs';

/**
 * A duel: two cars, one question, one answer (SPEC-007).
 *
 * The pool is ENUMERATED rather than searched. Fourteen cars over seven
 * questions is 637 candidate pairings, and filtering them once is both cheaper
 * than rejecting random pairs until one is answerable and the only shape in
 * which "can this catalogue be duelled at all" is a question a test can ask. A
 * content pack that arrived with two cars and no production figures would leave
 * an empty pool, and an empty pool is a designed screen rather than a hang.
 *
 * Everything here is pure. `random` is a defaulted parameter for the same reason
 * every browser API in this repo is one: a module that reaches for `Math.random`
 * internally cannot be asked what it does with a given draw, and the whole of
 * `duelRun` is built on being able to ask exactly that.
 */

/**
 * The key a pairing is remembered by — the question and the two cars, never the
 * side they were shown on.
 *
 * Sorted, so the same two cars asked the same question is one entry however the
 * deal presented them. That is what makes `seen` mean "already asked" rather
 * than "already asked in that order".
 */
export function duelKey(specKey, carA, carB) {
    return `${specKey}:${[carA.id, carB.id].sort().join('|')}`;
}

/**
 * Every answerable duel in this catalogue.
 *
 * The pair is stored in id order and the DEAL decides which side each car is
 * shown on. A pool built deterministically and presented in build order would
 * put the winner on the left slightly more often than chance, for ever, and a
 * reader would notice that long before a test did.
 */
/**
 * How far apart these two are, as a fraction of the larger figure.
 *
 * Relative rather than absolute because it is compared across SUBJECTS: 44 km/h
 * and 44 hp are not the same size of difference, and the difficulty curve asks
 * one question of all eleven — "is this pairing close?".
 */
function relativeGap(pair, specKey) {
    const values = pair.map((car) => specValue(car, specKey) ?? 0);
    const largest = Math.max(...values);

    return largest > 0 ? (duelMargin(pair[0], pair[1], specKey) ?? 0) / largest : 0;
}

export function buildDuelPool(cars) {
    const catalogue = Array.isArray(cars) ? cars : [];
    const pool = [];

    DUEL_SPECS.forEach(({ key }) => {
        for (let left = 0; left < catalogue.length; left += 1) {
            for (let right = left + 1; right < catalogue.length; right += 1) {
                const pair = [catalogue[left], catalogue[right]]
                    .sort((one, other) => String(one.id).localeCompare(String(other.id)));
                const winner = duelWinner(pair[0], pair[1], key);

                // Null covers a missing figure, a tie and a gap too small to be
                // a question. None of the three is something a reader could be
                // right about, so none of them is in the pool.
                if (winner) {
                    pool.push({
                        key: duelKey(key, pair[0], pair[1]),
                        spec: key,
                        cars: pair,
                        winnerId: winner.id,
                        // Both carried ON the entry rather than recomputed when
                        // a round asks for one: the pool is built once and dealt
                        // from a dozen times, and the alternative is the same
                        // arithmetic run over 493 rows on every tap.
                        difficulty: duelSpec(key).difficulty,
                        gap: relativeGap(pair, key),
                    });
                }
            }
        }
    });

    return pool;
}

/**
 * Deal one duel: at this round's difficulty where it can, and preferring a
 * pairing this run has not asked yet.
 *
 * The ROUND is what makes the game get harder — early rounds only draw the
 * easiest subjects and only pairings that are not close, and both open up as a
 * run goes on (`duelDifficulty`). A run that reaches the end of what it can be
 * asked starts over rather than deadlocking: a nineteen-round streak is a rarer
 * event than a bug that ends one, and a reader who has earned a repeat has
 * earned it.
 */
export function dealDuel(pool, { random = Math.random, seen = [], round = 1 } = {}) {
    const candidates = Array.isArray(pool) ? pool : [];

    if (candidates.length === 0) {
        return null;
    }

    const asked = new Set(seen);
    // Narrowed in order of what matters, and each step falls back to the one
    // before it rather than to nothing. A round with no question on it is the
    // worst outcome available here, so every filter is a PREFERENCE: ask
    // something at this round's difficulty that has not been asked; failing
    // that, anything at this difficulty; failing that, anything unseen; failing
    // that, anything at all.
    const from = firstNonEmpty([
        candidates.filter((duel) => isRoundCandidate(duel, round) && !asked.has(duel.key)),
        candidates.filter((duel) => isRoundCandidate(duel, round)),
        candidates.filter((duel) => !asked.has(duel.key)),
        candidates,
    ]);
    // Clamped rather than wrapped: a `random` that answers exactly 1 — which a
    // test double is entitled to do — would otherwise index past the end.
    const duel = from[Math.min(Math.floor(random() * from.length), from.length - 1)];
    // The second draw is the SIDE, and it is a separate one on purpose: the
    // pool's own order is deterministic, so a deal that did not shuffle here
    // would seat the winner by alphabet.
    const flipped = random() < 0.5;

    return { ...duel, cars: flipped ? [duel.cars[1], duel.cars[0]] : [...duel.cars] };
}

/** The first list with anything in it. The fallback chain, written once. */
function firstNonEmpty(lists) {
    return lists.find((list) => list.length > 0) ?? [];
}

/**
 * What the reader's tap was.
 *
 * The winner is read off the duel rather than recomputed, so the answer a reader
 * is graded against is exactly the one the pool was built from — a second
 * comparison here is a second place for the rounding to land differently.
 */
export function judgeDuel(duel, chosenCarId) {
    if (!duel) {
        return null;
    }

    const winner = duel.cars.find((car) => car.id === duel.winnerId) || null;
    const loser = duel.cars.find((car) => car.id !== duel.winnerId) || null;
    const chosen = duel.cars.find((car) => car.id === chosenCarId) || null;

    return {
        duel,
        spec: duel.spec,
        chosen,
        winner,
        loser,
        // A tap on nothing is not a correct answer. `chosen` is null only for a
        // caller passing an id that is not in this duel, which is a bug rather
        // than a reader — but it must not grade as a win.
        isCorrect: Boolean(chosen) && chosen.id === duel.winnerId,
    };
}
